import { CognitoUser } from '@aws-amplify/auth';
import { useUnit } from 'effector-react';
import $$auth from 'models/auth';
import { $queryParams } from 'models/location';
import { useState } from 'react';
import { Button, Card, Col, Form, Spinner } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { css } from 'styled-components';

import './MfaPage.scss';

import HookFormControl from 'components/HookFormControl';
import { Workspace, Wrapper } from 'components/pages/Dashboard/common/DashboardPageStyles';

import AlertsService from 'services/AlertsService';

import useResources from 'hooks/useResources';

import signin_page_image from 'assets/signin_page_image_big.png';
import signin_page_image_webp from 'assets/signin_page_image_big.webp';

type MfaPageProps = {
    user: CognitoUser;
    onAttemptsLimitReached: () => void;
    onBackToSignIn: () => void;
    onSuccess?: () => void;
};

const MfaPage = (props: MfaPageProps) => {
    const form = useForm();
    const { t } = useResources();
    const [loading, setLoading] = useState(false);
    const { session } = useUnit($queryParams);

    const onSubmit = async (data: any) => {
        setLoading(true);
        try {
            const result = await $$auth.sendCustomChallengeAnswerFx({ user: props.user, code: data.code });
            if (!result.signInUserSession) {
                AlertsService.showError(t('mfaPage.invalidCode'));
            } else {
                AlertsService.hideAll();
                if (props.onSuccess) {
                    props.onSuccess();
                }
            }
        } catch (e: any) {
            if (e.code === 'NotAuthorizedException') {
                AlertsService.showError(t('mfaPage.maxAttemptsExceeded'));
                props.onAttemptsLimitReached();
                return;
            }
            AlertsService.handleError(e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Workspace
            extra={css`
                @media (max-width: 476px) {
                    justify-content: flex-start;
                }
            `}
        >
            <Wrapper
                extra={css`
                    flex-direction: row;
                    width: 100%;
                    justify-content: space-between;
                    align-items: center;
                    @media (max-width: 1024px) {
                        justify-content: center;
                    }
                    @media (max-width: 476px) {
                        align-items: flex-start;
                        margin: unset;
                        margin-top: 24px;
                    }
                `}
            >
                <Card className="mfa-page">
                    <h4 className="font-weight-bold text-left">{t('mfaPage.header')}</h4>
                    <FormProvider {...form}>
                        <Form onSubmit={form.handleSubmit(onSubmit)}>
                            <Form.Row className="justify-content-center">
                                <Form.Group as={Col}>
                                    <HookFormControl.Text
                                        name="code"
                                        label={t('mfaPage.verificationCode')}
                                        rules={{
                                            required: t('mfaPage.verificationCodeError'),
                                        }}
                                    />
                                </Form.Group>
                            </Form.Row>
                            <Button
                                data-qaid="verify_button"
                                type="submit"
                                variant="primary"
                                className="mb-2"
                                disabled={loading}
                                block
                            >
                                {t('mfaPage.verify')}{' '}
                                {loading && (
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                )}
                            </Button>
                            <Button
                                data-qaid="change_email_button"
                                variant="light"
                                className="mb-2"
                                disabled={loading}
                                block
                                onClick={() => {
                                    props.onBackToSignIn();
                                }}
                            >
                                {t('mfaPage.backToSignIn')}{' '}
                            </Button>
                        </Form>
                    </FormProvider>
                </Card>
                <div className="col-sm image-holder d-none d-sm-flex">
                    <picture>
                        <source srcSet={signin_page_image_webp} type="image/webp" />
                        <img src={signin_page_image} className="img-fluid" alt="sign-in" />
                    </picture>
                </div>
            </Wrapper>
        </Workspace>
    );
};

export default MfaPage;
